import React, { useRef, useEffect } from 'react';

interface SortDropdownProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  setSortOrder: React.Dispatch<React.SetStateAction<string>>;
  sortBy: string;
  sortOrder: string;
}

const SortDropdown: React.FC<SortDropdownProps> = ({ isOpen, setIsOpen, setSortBy, setSortOrder, sortBy, sortOrder }) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  const handleClickOutside = (event: PointerEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('pointerdown', handleClickOutside);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, []);

  if (!isOpen) return null;

  // Function to handle sorting option click
  const handleSortChange = (sortOption: string) => {
    if (sortBy === sortOption) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); // Toggle order if the same option is clicked
    } else {
      setSortBy(sortOption);
      setSortOrder('asc'); // Default to ascending when a new option is selected
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        id="dropdown-3"
        className="z-10 bg-white divide-y divide-gray-100 rounded-lg shadow w-56 absolute mt-2"
        style={{ top: '100%', left: '-184px' }}
      >
        <ul className="px-4 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton">
          {/* 🔹 Sort By Title with Clickable Sorting Icon */}
          <p className="py-4 border-b border-gray-200 font-semibold text-gray-700 flex justify-between items-center">
            Sort By
            <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
              <img
                src={`/images/sort-${sortOrder}.svg`}
                alt={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                className={`w-5 h-5 cursor-pointer transition-transform ${
                  sortOrder === 'asc' ? 'rotate-0' : 'rotate-180'
                }`}
              />
            </button>
          </p>

          {/* Sort options */}
          {[
            // { key: 'priority', icon: 'st-priority', label: 'Priority' },
            { key: 'departure', icon: 'st-arrow-slant', label: 'Departure Time' },
            { key: 'route', icon: 'st-route', label: 'Route' },
            { key: 'flightNumber', icon: 'st-flight-slim', label: 'Flight Number' },
            { key: 'duration', icon: 'st-time', label: 'Duration' },
            { key: 'airline', icon: 'st-flight', label: 'Airline' },
            { key: 'arrival', icon: 'st-arrow-slant', label: 'Arrival', rotation: 'rotate-90' }
          ].map(({ key, icon, label, rotation }) => (
            <li
              key={key}
              onClick={() => handleSortChange(key)}
              className="flex items-center gap-2 py-2 px-3 cursor-pointer"
            >
              <i className={`${icon} text-sm ${rotation || ''} ${sortBy === key ? 'text-[#FFBC00]' : 'text-lightgray-150'}`}></i>
              <p className={`text-sm ${sortBy === key ? 'text-[#FFBC00] font-semibold' : 'text-lightgray-200'}`}>
                {label}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SortDropdown;
